/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useAsyncDebounce } from "react-table";
import { api } from "core/api";
import { Card, Button, Input, SvgIcon, ControlledTable } from "core/components";
import { admin_item_inventory_path, warehouse_admin_item_inventories_path } from "routes";
import ItemInventoryForm from "./ItemInventoryForm";
import ControlledTableContext from "core/components/ControlledTableContext";

type ItemInventoryProps = {
  warehouseId: null | number;
  policy: Record<string, boolean>;
};

const ItemInventory = ({
  warehouseId,
  warehouses,
  actions,
  adjustment_reasons,
  isItemsLoading,
  items,
  policy,
  onSave
}: ItemInventoryProps): JSX.Element => {
  const [itemInventory, setItemInventory] = React.useState([]);
  const [showItemInventoryModal, setShowItemInventoryModal] = React.useState(false);
  const [inventoryItem, setInventoryItem] = React.useState(null);
  const actionColumn = policy.update ? [] : ["action"];
  const [isLoading, setIsLoading] = React.useState(false);
  const [pagination, setPagination] = React.useState(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const table = React.useRef(null);
  const abortController = React.useRef<AbortController>();
  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id"
      },
      {
        id: "item_code",
        Header: "Inventory ID",
        accessor: "item_code",
        Cell: (props) => {
          return <p className="text-center">{props.value}</p>;
        }
      },
      {
        id: "item_name",
        Header: "Inventory",
        accessor: "item_name",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "UOM",
        accessor: "unit_measure_code",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Total Quantity",
        accessor: "total_quantity",
        Cell: (props) => {
          const totalQuantity = (+props.value || 0).toLocaleString("en-US", { minimumFractionDigits: 1 });
          return (
            <div title={totalQuantity} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{totalQuantity}</p>
            </div>
          );
        }
      },
      {
        Header: "Available Quantity",
        accessor: "available_quantity",
        Cell: (props) => {
          const availableQuantity = (+props.value || 0).toLocaleString("en-US", { minimumFractionDigits: 1 });
          return (
            <div title={availableQuantity} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{availableQuantity}</p>
            </div>
          );
        }
      },
      {
        Header: "Allocated Quantity",
        accessor: "reserved_quantity",
        Cell: (props) => {
          const allocatedQuantity = (+props.value || 0).toLocaleString("en-US", { minimumFractionDigits: 1 });
          return (
            <div title={allocatedQuantity} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{allocatedQuantity}</p>
            </div>
          );
        }
      },
      {
        Header: "Reorder Point",
        accessor: "reorder_point",
        Cell: (props) => {
          const reorderPoint = (+props.value || 0).toLocaleString("en-US", { minimumFractionDigits: 1 });
          return (
            <div title={reorderPoint} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{reorderPoint}</p>
            </div>
          );
        }
      },
      {
        Header: "Safety Stock Level",
        accessor: "safety_stock_level",
        Cell: (props) => {
          const safetyStockLevel = (+props.value || 0).toLocaleString("en-US", { minimumFractionDigits: 1 });
          return (
            <div title={safetyStockLevel} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{safetyStockLevel}</p>
            </div>
          );
        }
      },
      {
        id: "action",
        Header: "Action",
        accessor: "",
        Cell: (props) => {
          return (
            <div className="d-flex justify-content-center">
              <a
                className="button button--action button--action-approve"
                title="View Inventory"
                data-bs-toggle="tooltip"
                onClick={() => {
                  setInventoryItem(props.row.original);
                  window.open(admin_item_inventory_path(props.row.original.id, { warehouse_id: warehouseId }));
                }}
              >
                <SvgIcon name="view" className="h5" />
              </a>
              <a
                className="button button--action button--action-success"
                title="Update Inventory"
                data-bs-toggle="tooltip"
                onClick={() => {
                  setInventoryItem(props.row.original);
                  setShowItemInventoryModal(!showItemInventoryModal);
                }}
              >
                <SvgIcon name="edit" className="h5" />
              </a>
            </div>
          );
        }
      }
    ],
    []
  );

  const getCellProps = (cellInfo) => {
    if (cellInfo) {
      const availableQuantity = parseFloat(cellInfo.row.original.available_quantity);
      const reorderPoint = parseFloat(cellInfo.row.original.reorder_point);
      const safetyStockLevel = parseFloat(cellInfo.row.original.safety_stock_level);

      if (availableQuantity < safetyStockLevel && availableQuantity > reorderPoint) {
        return {
          style: {
            color: "#ffc100",
            fontWeight: "bold"
          }
        };
      } else if (availableQuantity < reorderPoint) {
        return {
          style: {
            color: "#f46a6a",
            fontWeight: "bold"
          }
        };
      } else {
        return {};
      }
    }
    return {};
  };

  const getItemInventory = React.useCallback(async ({ page, per, filters }) => {
    try {
      setIsLoading(true);
      // If there is a pending fetch request with associated AbortController, abort
      if (abortController.current) abortController.current.abort();
      // Assign a new AbortController for the latest fetch to our useRef variable
      abortController.current = new AbortController();
      const { signal } = abortController.current;

      const response = await api.get(
        warehouse_admin_item_inventories_path({ warehouse_id: warehouseId, page, per, filters: JSON.stringify(filters) }),
        { signal }
      );
      setItemInventory(response.data.data);
      setPagination(response.data.pagination);
      setCurrentPage(response.data.pagination?.current || currentPage);
      return { pagination: response.data.pagination, data: response.data.data };
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const onChange = useAsyncDebounce((column, value) => {
    table.current.setFilter(column, value);
  }, 400);

  const noContent = () => {
    return (
      <Container fluid={true} className="text-center pt-lg-8 pb-lg-8">
        <Row>
          <Col>
            <SvgIcon name="clipboard-list" className="display-icon text-gray" />
          </Col>
        </Row>
        <Row>
          <Col>
            <h5>No item inventories.</h5>
          </Col>
        </Row>
      </Container>
    );
  };

  const dataFilter = () => {
    return (
      <Row className="mb-3">
        <Col>
          <Input placeholder="Search for Inventory ID" onChange={(e) => onChange("item_code", e.target.value)} />
        </Col>
        <Col>
          <Input placeholder="Search for Inventory Name" onChange={(e) => onChange("item_name", e.target.value)} />
        </Col>
        {policy.update && (
          <Col sm="auto">
            <Button
              className="button--brand"
              title="Update Inventory"
              icon="edit"
              onClick={() => {
                setInventoryItem(null);
                setShowItemInventoryModal(!showItemInventoryModal);
              }}
            />
          </Col>
        )}
      </Row>
    );
  };

  const dataTable = () => {
    return (
      <Row>
        <Col>
          <ControlledTableContext.Provider value={{ currentPage, setCurrentPage }}>
            <ControlledTable
              columns={columns}
              data={itemInventory}
              ref={table}
              hiddenColumns={[...actionColumn]}
              getCellProps={getCellProps}
              isLoading={isLoading}
              fetchData={getItemInventory}
              totalCount={pagination?.total_count}
              totalPageCount={pagination?.total_pages || 0}
              previous={pagination?.previous}
              next={pagination?.next}
            />
          </ControlledTableContext.Provider>
        </Col>
      </Row>
    );
  };

  const content = () => {
    return warehouseId ? (
      <>
        {dataFilter()}
        {dataTable()}
      </>
    ) : (
      noContent()
    );
  };

  return (
    <React.StrictMode>
      <Card.Body>
        <Container fluid={true}>{content()}</Container>
      </Card.Body>
      <ItemInventoryForm
        warehouses={warehouses}
        warehouse_id={warehouseId}
        actions={actions}
        adjustment_reasons={adjustment_reasons}
        inventoryItem={inventoryItem}
        isItemsLoading={isItemsLoading}
        items={items}
        itemInventory={itemInventory}
        show={showItemInventoryModal}
        onClose={() => setShowItemInventoryModal(!showItemInventoryModal)}
        onSave={(itemInventory) => {
          onSave(itemInventory);
          getItemInventory({ page: 1, per: pagination?.limit || 10, filters: table.current.state.filters });
          setShowItemInventoryModal(!showItemInventoryModal);
        }}
      />
    </React.StrictMode>
  );
};

export default ItemInventory;
